import { useState, useEffect, lazy, Suspense } from "react";
import Dashboard from "./components/Dashboard";
import Stats from "./components/Stats";
import Profile from "./components/Profile";
import Premium from "./components/Premium";
import EditMeal from "./components/EditMeal";
import Plan from "./components/Plan";
import BottomNavigation from "./components/BottomNavigation";
import QuickActionsSheet from "./components/QuickActionsSheet";
import PageTransition from "./components/ui/PageTransition";
import { ToastProvider } from "./components/ui/Toast";
import ShimmerSkeleton from "./components/ui/ShimmerSkeleton";
import telegramService from "./utils/telegram";
import apiService from "./services/api";
import i18n from "./i18n";
import {
  NavigationProvider,
  useNavigation,
  type ScreenEntry,
  type Tab,
} from "./navigation/NavigationContext";
import "./App.css";

const Lenta = lazy(() => import("./components/Lenta"));
const MealPlan = lazy(() => import("./components/MealPlan"));
const Recipes = lazy(() => import("./components/Recipes"));
const ChatCoach = lazy(() => import("./components/ChatCoach"));

/** Bottom nav yashirinadigan screen'lar (masalan to'liq ekran chat) */
const HIDE_NAV_SCREENS = new Set<ScreenEntry["id"]>(["chat_coach"]);

function ScreenView({ screen }: { screen: ScreenEntry }) {
  const { pop } = useNavigation();

  switch (screen.id) {
    case "premium":
      return <Premium />;
    case "meal_plan":
      return (
        <Suspense fallback={<ShimmerSkeleton variant="mealPlan" />}>
          <MealPlan />
        </Suspense>
      );
    case "recipes":
      return (
        <Suspense fallback={<ShimmerSkeleton variant="recipes" />}>
          <Recipes />
        </Suspense>
      );
    case "chat_coach":
      return (
        <Suspense fallback={<ShimmerSkeleton variant="chat" />}>
          <ChatCoach onBack={pop} />
        </Suspense>
      );
    case "edit_meal":
      return screen.params?.mealId ? (
        <EditMeal mealId={screen.params.mealId} onSave={pop} onCancel={pop} />
      ) : null;
    case "lenta":
      return (
        <Suspense fallback={<ShimmerSkeleton variant="friends" />}>
          <Lenta />
        </Suspense>
      );
    default:
      return null;
  }
}

function TabView({
  tab,
  onChromeHiddenChange,
}: {
  tab: Tab;
  onChromeHiddenChange: (hidden: boolean) => void;
}) {
  switch (tab) {
    case "home":
      return <Dashboard onChromeHiddenChange={onChromeHiddenChange} />;
    case "plan":
      return <Plan />;
    case "stats":
      return <Stats />;
    case "profile":
      return <Profile />;
  }
}

function AppContent() {
  const { tab, screen, setTab, push } = useNavigation();
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [flowChromeHidden, setFlowChromeHidden] = useState(false);

  useEffect(() => {
    const syncLanguage = async () => {
      try {
        const tgId = telegramService.getUserId();
        if (!tgId) return;
        const userData = await apiService.getUser(tgId);
        if (userData.language && userData.language !== i18n.language) {
          i18n.changeLanguage(userData.language);
        }
      } catch (error) {
        console.error("Error syncing language:", error);
      }
    };
    syncLanguage();

    const params = new URLSearchParams(window.location.search);
    const urlStartParam = params.get("start_param");
    const telegramStartParam =
      window.Telegram?.WebApp?.initDataUnsafe?.start_param;
    const startParam = urlStartParam || telegramStartParam;

    if (startParam) {
      if (startParam.startsWith("edit_meal_")) {
        const mealId = startParam.replace("edit_meal_", "");
        push("edit_meal", { mealId });
      } else if (startParam === "premium_view") {
        push("premium");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavClick = (item: string) => {
    if (item === "log") {
      setQuickActionsOpen(true);
      return;
    }
    setFlowChromeHidden(false);
    setTab(item as Tab);
  };

  const hideNav =
    flowChromeHidden || (screen !== null && HIDE_NAV_SCREENS.has(screen.id));

  return (
    <div className={`app min-h-screen bg-app text-content-primary overflow-x-hidden ${hideNav ? "" : "pb-24"}`}>
      <PageTransition activeKey={screen ? `screen-${screen.id}` : `tab-${tab}`}>
        {screen ? (
          <ScreenView screen={screen} />
        ) : (
          <TabView tab={tab} onChromeHiddenChange={setFlowChromeHidden} />
        )}
      </PageTransition>
      {!hideNav && (
        <BottomNavigation
          activeTab={screen ? "" : tab}
          onTabChange={handleNavClick}
        />
      )}
      <QuickActionsSheet
        isOpen={quickActionsOpen}
        onClose={() => setQuickActionsOpen(false)}
      />
    </div>
  );
}

function App() {
  return (
    <ToastProvider>
      <NavigationProvider>
        <AppContent />
      </NavigationProvider>
    </ToastProvider>
  );
}

export default App;
